import {
  required,
  ResourceContextProvider,
  SimpleForm,
  TextInput,
} from "react-admin";
import {
  useFormContext,
  type FieldValues,
  type SubmitHandler,
} from "react-hook-form";

import { BaseModal } from "@/components/modal";
import { MODULE_NAMES } from "@/constants/modules";
import { formatDateToStandard } from "@/fields/DateFieldWithStandardFormat/utils/formatDateToStandard";
import { ReferenceSelectArrayInput } from "@/inputs/ReferenceSelectArrayInput";
import { ClientAppAllowedLanding } from "@/modules/clientApps/components/ClientAppAllowedLanding";

import { CampaignTdsFormToolbar } from "./CampaignTdsFormToolbar";
import { type CampaignTdsFormShape } from "./types";

export type CampaignTdsRecordToEdit = {
  id: number;
  landing: string;
  countrySet: string[];
  modelSet: string[];
  osSet: string[];
};

type Props = {
  isOpen: boolean;
  isPending?: boolean;
  record: Partial<CampaignTdsRecordToEdit> | null;
  withDates?: boolean;
  onClose: () => void;
  onSubmit: SubmitHandler<CampaignTdsRecordToEdit>;
};

export const CampaignTdsEditFormModal = ({
  isOpen,
  isPending,
  record,
  withDates,
  onClose,
  onSubmit,
}: Props) => {
  const { watch } = useFormContext<CampaignTdsFormShape>();
  const selectedClientAppId = watch("clientAppId");

  return (
    <BaseModal open={isOpen} title="Edit Campaign TDS" onClose={onClose}>
      <ResourceContextProvider value={MODULE_NAMES.CAMPAIGN_TDS}>
        <SimpleForm
          record={record ?? {}}
          disabled={isPending}
          onSubmit={onSubmit as SubmitHandler<FieldValues>}
          toolbar={
            <CampaignTdsFormToolbar onDelete={onClose} withDeleteButton />
          }
        >
          <ClientAppAllowedLanding
            selectedClientAppId={selectedClientAppId}
            validate={[required()]}
            source="landing"
          />
          <ReferenceSelectArrayInput
            module={MODULE_NAMES.COUNTRIES}
            source="countrySet"
            optionValue="code"
          />
          <ReferenceSelectArrayInput
            module={MODULE_NAMES.APPLE_MODELS}
            source="modelSet"
            optionText="title"
          />
          <ReferenceSelectArrayInput
            module={MODULE_NAMES.OS}
            source="osSet"
            optionText="title"
          />
          {withDates && (
            <TextInput
              source="createdAt"
              format={formatDateToStandard}
              readOnly
            />
          )}
          {withDates && (
            <TextInput
              source="updatedAt"
              format={formatDateToStandard}
              readOnly
            />
          )}
        </SimpleForm>
      </ResourceContextProvider>
    </BaseModal>
  );
};
